import { AsyncPipe, SlicePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
  catchError,
  concatMap,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { InstructionViewComponent } from 'src/app/components/instruction-view/instruction-view.component';
import { ArtifactApiService } from 'src/app/services/api/artifact-api.service';
import { ReleaseService } from 'src/app/services/release.service';
import {
  Artifact,
  Release,
  ReleaseDocumentationData,
  Response,
} from 'src/app/types';
import { ArtifactsHashTooltipComponent } from 'src/app/components/artifacts-hash-tooltip/artifacts-hash-tooltip.component';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { ReleaseApiService } from 'src/app/services/api/release-api.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzListModule } from 'ng-zorro-antd/list';
import { ClipboardService } from 'ngx-clipboard';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { DebVerificationModalComponent } from 'src/app/components/modals/deb-verification-modal.component';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
@Component({
  selector: 'app-release',
  templateUrl: './release-details.component.html',
  styleUrls: ['./release-details.component.scss'],
  imports: [
    NzTableModule,
    AsyncPipe,
    SlicePipe,
    NzPageHeaderModule,
    NzDescriptionsModule,
    NzButtonModule,
    NzCardModule,
    InstructionViewComponent,
    SlicePipe,
    NzTooltipModule,
    ArtifactsHashTooltipComponent,
    NzTagComponent,
    NzListModule,
    NzModalModule,
    NzIconModule,
    DebVerificationModalComponent,
    NzTypographyModule,
  ],
  standalone: true,
})
export class ReleaseDetailsComponent {
  private releaseApiService = inject(ReleaseApiService);
  private artifactApiService = inject(ArtifactApiService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(NzMessageService);
  private clipboardService = inject(ClipboardService);
  releaseService = inject(ReleaseService);

  id: number = 0;
  isDownloadingAllArtifacts = false;
  isDownloadingAllDocs = false;
  isOpenPullModal = false;
  isOpenVerificationModal = false;
  activeDocumentIndex = 0;
  pullCommand = '';
  artifactsToDownload: Artifact[] = [];
  documentationsToDownload: ReleaseDocumentationData[] = [];

  documentations = this.releaseService.documentations;

  instruction$ = this.releaseService.instruction$;
  releaseNotes$ = this.releaseService.releaseNotes$;
  artifacts$ = this.releaseService.artifacts$;
  backendArtifacts$ = this.releaseService.backendArtifacts$;
  releaseVersion$ = this.releaseService.releaseVersion$;
  loading$ = this.releaseService.loading$;

  downloadArtifactSubj$ = new Subject<Artifact>();
  downloadReleaseDocSubj$ = new Subject<ReleaseDocumentationData>();

  downloadArtifact$: Observable<any> = this.downloadArtifactSubj$.pipe(
    concatMap((artifact) =>
      this.artifactApiService
        .downloadFileById(
          artifact.id,
          this.releaseService.releaseId.value,
          artifact.contentName!,
        )
        .pipe(
          finalize(() => {
            this.artifactsToDownload = this.artifactsToDownload.filter(
              (a) => a.id !== artifact.id,
            );
          }),
        ),
    ),
  );

  downloadReleaseDoc$: Observable<any> = this.downloadReleaseDocSubj$.pipe(
    concatMap((doc) =>
      this.releaseApiService
        .downloadReleaseDoc({
          releaseId: this.id,
          contentName: doc.documentationFileName,
          contentKey: doc.documentationFileKey,
        })
        .pipe(
          finalize(() => {
            this.documentationsToDownload =
              this.documentationsToDownload.filter(
                (d) => d.documentationFileName !== doc.documentationFileName,
              );
          }),
        ),
    ),
  );

  release$: Observable<Response<Release>> = this.activatedRoute.params.pipe(
    switchMap((params) => {
      this.releaseService.releaseVersion.next('');
      this.releaseService.artifacts.next([]);
      this.releaseService.backendArtifacts.next([]);
      this.releaseService.documentations.next([]);

      this.id = params['id'];
      return this.releaseService.getRelease(this.id);
    }),
  );

  action$?: Observable<any>;

  removeProjectGroup(projectName: string | null): string {
    if (!projectName) return '';

    const startIndex = projectName.lastIndexOf('/');
    return projectName.slice(startIndex + 1, projectName.length);
  }

  download(artifact: Artifact): void {
    this.artifactsToDownload.push(artifact);
    this.downloadArtifactSubj$.next(artifact);
  }

  getIsArtifactDownloading(artifact: Artifact): boolean {
    return !!this.artifactsToDownload.find((art) => art.id === artifact.id);
  }

  getIsDocumentationDownloading(
    documentation: ReleaseDocumentationData,
  ): boolean {
    return !!this.documentationsToDownload.find(
      (doc) => doc.documentationFileKey === documentation.documentationFileKey,
    );
  }

  getDescriptionTooltipTitle(projectName: string, description: string): string {
    const descriptionString = !!description ? description : '';

    return this.removeProjectGroup(projectName) + '\n' + descriptionString;
  }

  goBack(): void {
    const { page, pageSize } = this.activatedRoute.snapshot.params;

    const url = this.router.url;
    const match = url.match(/\/product\/(\d+)/);
    const productId = match ? match[1] : 0;

    if (!!productId) {
      this.router.navigate([`/product/${productId}`], {
        queryParams: {
          page,
          pageSize,
        },
      });
    } else {
      this.router.navigate([`/releases`], {
        queryParams: {
          page,
          pageSize,
        },
      });
    }
  }

  getTagColor(publishComment: string): string {
    return publishComment.includes('Опубликован') ? 'default' : 'green';
  }

  downloadReleaseDoc(
    documentationFileName: string,
    documentationFileKey: string,
  ): void {
    this.documentationsToDownload.push({
      documentationFileName,
      documentationFileKey,
    });
    this.downloadReleaseDocSubj$.next({
      documentationFileName,
      documentationFileKey,
    });
  }

  downloadAllArtifacts(): void {
    this.isDownloadingAllArtifacts = true;
    let observables$: Observable<any>[] = [];

    this.releaseService.artifacts.value.forEach(({ id, contentName }) => {
      observables$.push(
        this.artifactApiService.downloadFileById(
          id,
          this.releaseService.releaseId.value,
          contentName!,
        ),
      );
    });

    this.action$ = forkJoin(observables$).pipe(
      tap(() => {
        this.isDownloadingAllArtifacts = false;
        this.messageService.success('Загрузка артефактов завершена');
      }),
      catchError(() => {
        this.isDownloadingAllArtifacts = false;
        this.messageService.error('Ошибка загрузки');
        return of();
      }),
    );
  }

  downloadAllDocs(): void {
    this.isDownloadingAllDocs = true;
    let observables$: Observable<any>[] = [];

    this.documentations.value.forEach(
      ({ documentationFileName, documentationFileKey }) => {
        observables$.push(
          this.releaseApiService.downloadReleaseDoc({
            releaseId: this.releaseService.releaseId.value,
            contentName: documentationFileName,
            contentKey: documentationFileKey,
          }),
        );
      },
    );

    this.action$ = forkJoin(observables$).pipe(
      tap(() => {
        this.isDownloadingAllDocs = false;
        this.messageService.success('Загрузка документации завершена');
      }),
      catchError(() => {
        this.isDownloadingAllDocs = false;
        this.messageService.error('Ошибка загрузки');
        return of();
      }),
    );
  }

  openPullModal(command: string): void {
    this.isOpenPullModal = true;
    this.pullCommand = command;
  }

  closePullModal(): void {
    this.isOpenPullModal = false;
    this.pullCommand = '';
  }

  copyPullCommand(): void {
    this.clipboardService.copy(this.pullCommand);
    this.messageService.info('Команда скопирована в буфер обмена');
  }

  openVerificationModal(): void {
    this.isOpenVerificationModal = true;
  }
}
